import { clsx, type ClassValue } from "clsx"
import { fmtFixed, intlLocale, ltr, stripLtr } from '@/lib/format';
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/****************
 * NUMBERS BUILT BY HAND, IN THE USER'S LANGUAGE. 2026-09-26.
 *
 * localiseDecimal: a toPrecision() string with the language's decimal mark
 * ("0.00043210" -> "0,00043210" in French). toPrecision is kept because its
 * significant-figure rule is the one the price columns were tuned to.
 *
 * withUsd: puts the dollar sign where the language puts it, around a number
 * that is already formatted ("1,234.56" -> "$1,234.56", "1 234,56 $" in
 * French). A negative reads "-$3.63", never "$-3.63". English is otherwise
 * exactly '$' + text.
 ****************/
export function localiseDecimal(text: string): string {
  const mark = new Intl.NumberFormat(intlLocale()).formatToParts(1.5).find((p) => p.type === 'decimal')?.value ?? '.'
  return mark === '.' ? text : text.replace('.', mark)
}

export function withUsd(input: string): string {
  const text = stripLtr(input)
  const negative = text.startsWith('-')
  const body = negative ? text.slice(1) : text
  const parts = new Intl.NumberFormat(intlLocale(), { style: 'currency', currency: 'USD', currencyDisplay: 'narrowSymbol' })
    .formatToParts(negative ? -1 : 1)
  let out = ''
  let placed = false
  for (const part of parts) {
    if (part.type === 'integer' || part.type === 'group' || part.type === 'decimal' || part.type === 'fraction') {
      if (!placed) { out += body; placed = true }
    } else {
      out += part.value
    }
  }
  return ltr(out)
}

/****************
 * Price / size formatting
 *
 * Smart price formatter so low-market-cap assets don't collapse to "0".
 * - large (>=1000): up to 2 decimals (1,234.56)
 * - mid (1-1000):   5 decimals (12.34560)
 * - small (<1):     at least 5 significant figures (0.00043210)
 * Min 5 decimals/sig-figs everywhere so micro-cap prices render real values.
 * 2026-06-19 (tester: "chart price decimals too coarse, min 5 decimals").
 ****************/
export function formatPrice(value: number | string | null | undefined): string {
  const p = typeof value === 'string' ? parseFloat(value.replace(/[$,]/g, '')) : value
  if (p == null || !isFinite(p)) return '—'
  if (p === 0) return '0'
  const abs = Math.abs(p)
  if (abs >= 1000) return p.toLocaleString(intlLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  if (abs >= 1) return fmtFixed(p, 5)
  // sub-$1 — guarantee at least 5 significant figures (e.g. 0.0000054321)
  return localiseDecimal(p.toPrecision(5))
}

/****************
 * A PRICE WE DO NOT HAVE, SAID AS NOT HAVING IT.
 *
 * formatPrice already returns "—" for null and undefined. Every caller was
 * handing it `value || 0`, which turns an absent price into a real zero before
 * it ever gets there, so the page printed $0.00 for an exit we never learned.
 *
 * ZERO IS NOT A PRICE. The backend writes 0 when it does not know -- F-31's own
 * note: "session 255's BTC close would render as an exit at $0.00" -- and no
 * contract trades at zero. So for a price, absent and zero are the same claim
 * and both read as unknown. Deleting the `|| 0` at the call sites would not
 * have been enough on its own; the rows where a zero is already stored would
 * still print $0.00.
 *
 * THE DOLLAR SIGN IS INSIDE. Callers wrote `${formatPrice(x)}`, so an unknown
 * price rendered as "$—". A currency symbol in front of an absence is a number
 * that is not there.
 *
 * FOR PRICES ONLY. A realised PnL of exactly 0 is a real result -- a close that
 * broke even -- and rendering that as "—" would invent an absence.
 ****************/
export function priceOrUnknown(value: number | string | null | undefined): string {
  const p = typeof value === 'string' ? parseFloat(value.replace(/[$,]/g, '')) : value
  if (p == null || !isFinite(p) || p === 0) return '—'
  return withUsd(formatPrice(p))
}

/****************
 * WHOSE PRICE IS ON THIS ORDER ROW.
 *
 * execution_price is the BEST available number, not the broker's: finalizeOrder
 * writes the venue's fill price when it has one and the COPIED TRADER's price
 * when it does not, on every status including failed. A row could therefore
 * show a price that never happened on this user's account, with nothing to say
 * so.
 *
 * executed_price is the fact -- written only on a venue-confirmed fill and NULL
 * otherwise. F-31 added it and its own comment records that nothing read it
 * yet. This reads it.
 *
 * Three outcomes, not two, because "we do not know" and "this is practice" are
 * different things and neither is a price:
 *
 *   broker     the exchange's own number
 *   simulated  a practice fill, shown and LABELLED -- a practice session with
 *              every price blanked is useless, and a practice price presented
 *              as a broker's is a lie
 *   unknown    a real order the exchange has not confirmed
 ****************/
export type PriceOrigin = 'broker' | 'simulated' | 'unknown'

/**
 * A practice order is identified by what WROTE it: the paper path stamps
 * "paper-<clOrdID>" into exchange_order_id and the simulated-trader path
 * "sim-...". Those are the two prefixes the backend invariants exclude when
 * they ask what is real.
 */
export function isSimulatedOrder(exchangeOrderId: string | null | undefined): boolean {
  const id = exchangeOrderId ?? ''
  return id.startsWith('paper-') || id.startsWith('sim-')
}

export interface OrderPriceView { text: string; origin: PriceOrigin }

export function orderPrice(order: {
  exchange_order_id?: string | null
  executed_price?: number | string | null
  execution_price?: number | string | null
}): OrderPriceView {
  if (isSimulatedOrder(order.exchange_order_id)) {
    const text = priceOrUnknown(order.execution_price)
    return { text, origin: text === '—' ? 'unknown' : 'simulated' }
  }
  const text = priceOrUnknown(order.executed_price)
  return { text, origin: text === '—' ? 'unknown' : 'broker' }
}

/** Size/amount formatter — micro-priced symbols carry sub-0.0001 sizes. */
export function formatSize(value: number | null | undefined): string {
  if (value == null || !isFinite(value)) return '—'
  const abs = Math.abs(value)
  if (abs === 0) return '0'
  if (abs >= 1) return fmtFixed(value, 4)
  return localiseDecimal(value.toPrecision(5))
}
